import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { surveysApi } from '../lib/api-client';
import type { SurveyAnswers } from '../types/api';

const FIVE_MINUTES = 5 * 60 * 1000;

export const useSurveys = () => {
  return useQuery({
    queryKey: ['surveys'],
    queryFn: () => surveysApi.list(),
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
  });
};

export const useSurvey = (surveyId: string | undefined) => {
  return useQuery({
    queryKey: ['surveys', surveyId],
    queryFn: () => surveysApi.getById(surveyId!),
    enabled: !!surveyId,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
  });
};

export const useSurveyTracking = (surveyId: string | undefined, locationId?: string) => {
  return useQuery({
    queryKey: ['surveys', surveyId, 'tracking', locationId ?? null],
    queryFn: () => surveysApi.getTracking(surveyId!, locationId),
    enabled: !!surveyId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useSurveyResponse = (surveyId: string | undefined, locationId: string | undefined) => {
  return useQuery({
    queryKey: ['surveys', surveyId, 'responses', locationId],
    queryFn: () => surveysApi.getResponse(surveyId!, locationId!),
    enabled: !!surveyId && !!locationId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useSaveSurveyResponse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, locationId, answers }: { surveyId: string; locationId: string; answers: SurveyAnswers }) =>
      surveysApi.saveResponse(surveyId, locationId, answers),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys', variables.surveyId] });
    },
  });
};
